import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { trimsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import {
  Plus, Search, AlertTriangle, Package, Minus, CirclePlus,
  Pencil, Disc, Palette, Ruler
} from 'lucide-react';

const TYPE_CONFIG = {
  button: { label: 'Buttons', icon: Disc, color: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
  thread: { label: 'Threads', icon: Ruler, color: '#0E7490', bg: 'rgba(14,116,144,0.08)' },
  lining: { label: 'Linings', icon: Palette, color: '#B45309', bg: 'rgba(180,83,9,0.08)' },
  other: { label: 'Other', icon: Package, color: '#475467', bg: 'rgba(71,84,103,0.08)' },
};

const COLOUR_MAP = {
  'dark brown': '#5C3317', 'black': '#1a1a1a', 'white': '#F5F5F5', 'smoke grey': '#8B8B8B',
  'navy': '#1B2A4A', 'gold': '#C5A55A', 'burgundy': '#722F37', 'charcoal': '#36454F',
  'natural': '#D2B48C', 'red': '#8B2500', 'grey': '#808080', 'cream': '#FFFDD0',
  'brown': '#6B3A2A', 'blue': '#2A4E8B', 'green': '#2F5233', 'beige': '#C8B88A',
};

function getColourHex(colourName) {
  if (!colourName) return '#D1D5DB';
  const lower = colourName.toLowerCase();
  for (const [key, hex] of Object.entries(COLOUR_MAP)) {
    if (lower.includes(key)) return hex;
  }
  return '#D1D5DB';
}

export default function TrimsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trims, setTrims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showLowOnly, setShowLowOnly] = useState(false);

  const canEdit = user?.role === 'Admin';

  const fetchTrims = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (typeFilter !== 'all') params.trim_type = typeFilter;
      if (search) params.q = search;
      if (showLowOnly) params.low_stock = true;
      const res = await trimsAPI.list(params);
      setTrims(res.data);
    } catch (err) {
      toast.error('Failed to load trims');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, search, showLowOnly]);

  useEffect(() => { fetchTrims(); }, [fetchTrims]);

  const grouped = {
    button: trims.filter(t => t.trim_type === 'button'),
    thread: trims.filter(t => t.trim_type === 'thread'),
    lining: trims.filter(t => t.trim_type === 'lining'),
    other: trims.filter(t => t.trim_type === 'other'),
  };

  const lowStockCount = trims.filter(t => t.is_low_stock).length;

  return (
    <div className="space-y-6" data-testid="trims-list-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--brand-navy)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
            Trims Inventory
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
            {trims.length} item{trims.length !== 1 ? 's' : ''}
            {lowStockCount > 0 && (
              <span className="ml-2 text-xs font-medium px-1.5 py-0.5 rounded-md" style={{ backgroundColor: 'rgba(220,38,38,0.08)', color: '#DC2626' }}>
                {lowStockCount} low stock
              </span>
            )}
          </p>
        </div>
        {canEdit && (
          <Button data-testid="add-trim-button" onClick={() => navigate('/app/trims/new')}
            className="h-10 rounded-xl px-5 text-sm font-medium" style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
            <Plus className="w-4 h-4 mr-2" /> Add Trim
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
          <Input
            data-testid="trim-search-input"
            placeholder="Search by name, material, or colour..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 h-10 rounded-lg text-sm"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px] h-10 rounded-lg text-sm" data-testid="trim-type-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="button">Buttons</SelectItem>
            <SelectItem value="thread">Threads</SelectItem>
            <SelectItem value="lining">Linings</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={showLowOnly ? 'default' : 'outline'}
          onClick={() => setShowLowOnly(p => !p)}
          className={`h-10 rounded-lg text-sm px-4 gap-1.5 ${showLowOnly ? '' : ''}`}
          data-testid="low-stock-filter"
          style={showLowOnly ? { backgroundColor: '#DC2626', color: 'white' } : {}}
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          Low Stock
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-44 rounded-xl" />)}
        </div>
      )}

      {/* Empty state */}
      {!loading && trims.length === 0 && (
        <Card className="rounded-xl border border-border/80 bg-white">
          <CardContent className="p-10 text-center">
            <Package className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--brand-navy)', opacity: 0.3 }} strokeWidth={1.5} />
            <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>No trims found</p>
            <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>Try adjusting your search or filters</p>
          </CardContent>
        </Card>
      )}

      {/* Grouped Grid */}
      {!loading && Object.entries(grouped).map(([type, items]) => {
        if (items.length === 0) return null;
        const config = TYPE_CONFIG[type];
        return (
          <div key={type} className="space-y-3">
            <div className="flex items-center gap-2">
              <config.icon className="w-4 h-4" style={{ color: config.color }} />
              <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: config.color }}>
                {config.label}
              </h2>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 rounded-md ml-1">
                {items.length}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map(trim => (
                <TrimCard key={trim.id} trim={trim} config={config} canEdit={canEdit} onRefresh={fetchTrims}
                  onNavigate={() => navigate(`/app/trims/${trim.id}`)} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TrimCard({ trim, config, canEdit, onRefresh, onNavigate }) {
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  const handleAdjust = async (direction) => {
    const qty = parseInt(adjustQty) || 0;
    if (qty <= 0) { toast.error('Enter a valid quantity'); return; }
    setAdjusting(true);
    try {
      const finalQty = direction === 'add' ? qty : -qty;
      await trimsAPI.adjustStock(trim.id, {
        quantity: finalQty,
        reason: adjustReason || (direction === 'add' ? 'Stock received' : 'Used in production'),
      });
      toast.success(`Stock ${direction === 'add' ? 'added' : 'reduced'}: ${qty} ${trim.unit}${qty !== 1 ? 's' : ''}`);
      setAdjustOpen(false);
      setAdjustQty('');
      setAdjustReason('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust stock');
    } finally {
      setAdjusting(false);
    }
  };

  const colourHex = getColourHex(trim.colour);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <Card
      data-testid={`trim-card-${trim.id}`}
      className="rounded-xl border border-border/80 bg-white hover:shadow-[0_2px_16px_rgba(16,24,40,0.08)] group overflow-hidden"
      style={{ transition: 'box-shadow 200ms' }}
    >
      {/* Image / Colour Swatch */}
      <div className="relative h-20 flex items-center justify-center cursor-pointer" onClick={onNavigate}
        style={{ backgroundColor: trim.image_url ? '#f8f8f8' : `${colourHex}18` }}>
        {trim.image_url ? (
          <img src={`${backendUrl}${trim.image_url}`} alt={trim.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full border-2" style={{ backgroundColor: colourHex, borderColor: `${colourHex}40` }} />
        )}
        {/* Low stock badge */}
        {trim.is_low_stock && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold"
            style={{ backgroundColor: 'rgba(220,38,38,0.9)', color: 'white' }}>
            <AlertTriangle className="w-2.5 h-2.5" /> LOW
          </div>
        )}
      </div>

      <CardContent className="p-3.5">
        {/* Name + Colour */}
        <h3 className="text-xs font-semibold leading-tight mb-1 cursor-pointer line-clamp-2" onClick={onNavigate}
          style={{ color: 'var(--navy-ink)' }}>{trim.name}</h3>
        <div className="flex items-center gap-1.5 mb-2">
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: colourHex }} />
          <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
            {trim.colour}{trim.material ? ` \u00b7 ${trim.material}` : ''}
          </span>
        </div>

        {/* Stock + Price row */}
        <div className="flex items-center justify-between">
          <Popover open={adjustOpen} onOpenChange={setAdjustOpen}>
            <PopoverTrigger asChild>
              <button
                data-testid={`stock-adjust-trigger-${trim.id}`}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold tabular-nums cursor-pointer
                  hover:ring-1 hover:ring-[color:var(--brand-gold)]`}
                style={{
                  backgroundColor: trim.is_low_stock ? 'rgba(220,38,38,0.08)' : 'var(--surface-2)',
                  color: trim.is_low_stock ? '#DC2626' : 'var(--navy-ink)',
                  transition: 'box-shadow 150ms',
                }}
              >
                {trim.stock_quantity} <span className="text-[9px] font-normal" style={{ color: 'var(--ink-muted)' }}>{trim.unit}s</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3 rounded-xl" align="start">
              <p className="text-xs font-semibold mb-2" style={{ color: 'var(--brand-navy)' }}>Quick Stock Adjustment</p>
              <p className="text-[10px] mb-2" style={{ color: 'var(--ink-muted)' }}>
                Current: {trim.stock_quantity} {trim.unit}{trim.stock_quantity !== 1 ? 's' : ''}
              </p>
              <div className="space-y-2">
                <Input
                  data-testid={`stock-qty-input-${trim.id}`}
                  type="number"
                  min="1"
                  value={adjustQty}
                  onChange={e => setAdjustQty(e.target.value)}
                  placeholder="Quantity"
                  className="h-9 rounded-lg text-sm"
                />
                <Input
                  data-testid={`stock-reason-input-${trim.id}`}
                  value={adjustReason}
                  onChange={e => setAdjustReason(e.target.value)}
                  placeholder="Reason (optional)"
                  className="h-9 rounded-lg text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    data-testid={`stock-add-btn-${trim.id}`}
                    onClick={() => handleAdjust('add')}
                    disabled={adjusting || !adjustQty}
                    size="sm"
                    className="flex-1 h-8 rounded-lg text-xs gap-1"
                    style={{ backgroundColor: '#059669', color: 'white' }}
                  >
                    <CirclePlus className="w-3.5 h-3.5" /> Received
                  </Button>
                  <Button
                    data-testid={`stock-use-btn-${trim.id}`}
                    onClick={() => handleAdjust('subtract')}
                    disabled={adjusting || !adjustQty}
                    size="sm"
                    variant="outline"
                    className="flex-1 h-8 rounded-lg text-xs gap-1"
                    style={{ color: '#DC2626', borderColor: 'rgba(220,38,38,0.3)' }}
                  >
                    <Minus className="w-3.5 h-3.5" /> Used
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <span className="text-[10px] tabular-nums" style={{ color: 'var(--ink-muted)' }}>
            {'\u20b9'}{trim.cost_price}/{trim.unit}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
